<template>
  <div
    :class="{
      'bg-white': current,
      'bg-grey-950': !current,
      [`border-2 ${massSignatureColors.bg} ${massSignatureColors.border}`]: selected,
    }"
    class="rounded flex"
  >
    <router-link
      :data-sentry-id="`HomeLetterFileLink-${letterFile.chronoNumber}`"
      :to="`letter-file/${letterFile.chronoNumber}`"
      :id="describedById"
      class="flex flex-col grow py-2 pl-2"
    >
      <div class="flex gap-1">
        <PBadge
          v-if="selected"
          size="sm"
          type="custom"
          :class="`${massSignatureColors.text} ${massSignatureColors.tagBg} [&>span]:overflow-hidden [&>span]:text-ellipsis`"
        >
          Sélectionné pour signature
        </PBadge>
        <PBadge
          v-else-if="letterFile.isUnsigned"
          size="sm"
          type="custom"
          :class="`${massSignatureColors.text} ${massSignatureColors.tagBg} [&>span]:overflow-hidden [&>span]:text-ellipsis`"
        >
          Non Signé
        </PBadge>
        <div v-else-if="letterFile.userTags.length">
          <PBadge
            v-for="userTag in letterFile.userTags"
            :key="userTag.id"
            size="sm"
            type="custom"
            :class="`${massSignatureColors.text} ${massSignatureColors.tagBg} [&>span]:overflow-hidden [&>span]:text-ellipsis`"
          >
            {{ userTag.type }}
          </PBadge>
        </div>
      </div>
      <div class="flex items-center mb-1v">
        <div class="text-lg font-bold line-clamp-2">
          {{ letterFile.name ? letterFile.name : "Parapheur sans titre" }}
        </div>
      </div>

      <div>
        <div class="flex flex-row items-center gap-1 mt-1b pb-1v">
          <AbsenceIndicator
            v-if="absence"
            :name="letterFile.from?.name ?? letterFile.current?.name ?? ''"
            :absence="absence"
            :civility="letterFile.current?.civility"
            class="inline"
          />
          <span class="text-grey-200 line-clamp-1">
            {{ letterFile.from ? `Provenant de ${letterFile.from.name}` : letterFile.current?.name }}
          </span>
        </div>
        <div class="flex flex-wrap gap-1 items-center">
          <PBadge
            v-for="(tag, index) in tags"
            :key="index"
            :type="tag.grey ? 'custom' : 'error'"
            class="max-w-full [&>span]:overflow-hidden [&>span]:text-ellipsis"
            :class="{
              'text-brown-cafe-creme-200-hover bg-brown-cafe-creme-950': tag.grey,
            }"
          >
            {{ tag.name }}
          </PBadge>
          <PBadge
            v-if="letterFile.status === LetterFileStatus.CC"
            type="custom"
            class="text-grey-425 bg-grey-850 max-w-full [&>span]:overflow-hidden [&>span]:text-ellipsis"
          >
            En Copie
          </PBadge>
          <DueInBadge :letter-file="letterFile" class="max-w-full [&>span]:overflow-hidden [&>span]:text-ellipsis" />
        </div>
      </div>
    </router-link>
    <KebabMenu
      @click.prevent
      class="mt-2 mr-2"
      placement="left"
      size="sm"
      :aria-label="`Action rapide sur le parapheur`"
      :aria-describedby="describedById"
    >
      <template #default="{ close }">
        <button
          :data-sentry-id="`HomeLetterFileKebabTake-${letterFile.chronoNumber}`"
          v-if="!current && !letterFile.draft && !letterFile.archived"
          @click="take()"
          class="text-start"
        >
          Je prends la main
        </button>
        <button
          :data-sentry-id="`HomeLetterFileKebabNext-${letterFile.chronoNumber}`"
          v-if="current && !letterFile.archived"
          @click="next(close)"
          class="text-start"
          :disabled="!letterFile.name"
          :class="{ 'text-grey-425 cursor-not-allowed': !letterFile.name }"
        >
          Passer à l'étape suivante
        </button>
        <button
          :data-sentry-id="`HomeLetterFileKebabCancel-${letterFile.chronoNumber}`"
          v-if="letterFile.isDeletable"
          @click="openDeleteLFModal(close)"
          class="text-start text-red-marianne-425"
        >
          Supprimer
        </button>
        <div class="flex items-center">
          <button
            :data-sentry-id="`HomeLetterFileKebabClone-${letterFile.chronoNumber}`"
            @click="isCloneModalOpen = true"
            class="text-start"
          >
            Dupliquer
          </button>
        </div>

        <button
          :data-sentry-id="`HomeLetterFileKebabMassSignSelect-${letterFile.chronoNumber}`"
          @click="
            emit('select');
            close();
          "
          :disabled="!letterFile.isMassSignable"
          class="text-start"
          :class="{
            'text-grey-625 cursor-not-allowed': !letterFile.isMassSignable,
          }"
          v-if="massSignatureStore.enabled && current"
        >
          <span v-if="!selected">Sélectionner pour signer</span>
          <span v-else>Déselectionner</span>
        </button>
      </template>
    </KebabMenu>

    <CloneModal :letter-file="letterFile" v-model="isCloneModalOpen"></CloneModal>
    <DeleteLetterFileModal
      :open="isDeleteLFModalOpen"
      @cancel="isDeleteLFModalOpen = false"
      @confirm="deleteLetterFile"
    />
  </div>
</template>

<script setup lang="ts">
import DeleteLetterFileModal from "@/app/components/DeleteLetterFileModal.vue";
import AbsenceIndicator from "@/app/views/LetterFileForm/Flux/AbsenceIndicator.vue";
import { PBadge, usePastaRouter } from "@pasta/box-v2";
import { computed, ref, useId } from "vue";
import { useLetterFileColor } from "../composable/useLetterFileColor";
import { type LetterFileHomeDto } from "../dto/home.dto";
import { type LetterFileDto, LetterFileStatus } from "../dto/letter-file.dto";
import { useLetterFileStore } from "../stores/letter-file.store";
import { useMassSignatureStore } from "../stores/mass-signature.store";
import { canGoNext } from "../views/LetterFileForm/utils";
import CloneModal from "./CloneModal.vue";
import DueInBadge from "./DueInBadge.vue";
import KebabMenu from "./KebabMenu.vue";

interface Props {
  letterFile: LetterFileHomeDto;
  current?: boolean;
  selected?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  current: false,
  selected: false,
});

const emit = defineEmits<{
  (event: "select"): void;
}>();

const letterFileStore = useLetterFileStore();
const pastaRouter = usePastaRouter();

const tags = computed(() => {
  const tags = (props.letterFile.tags || []).map(tag => {
    return { name: tag.name, grey: false };
  });

  if (props.letterFile.draft) tags.push({ name: "brouillon", grey: true });
  if (props.letterFile.status === LetterFileStatus.ARCHIVED) tags.push({ name: "archivé", grey: true });
  return tags;
});

const absence = computed(() => {
  return props.letterFile.current?.absence;
});

const describedById = useId();

const isCloneModalOpen = ref<boolean>(false);
const isDeleteLFModalOpen = ref<boolean>(false);

async function take() {
  await letterFileStore.take(props.letterFile.id);
  await pastaRouter.push(`letter-file/${props.letterFile.chronoNumber}`);
}

async function openDeleteLFModal(close: () => void) {
  isDeleteLFModalOpen.value = true;
  close();
}

async function deleteLetterFile() {
  await letterFileStore.delete(props.letterFile.id);
  await letterFileStore.getHome();
  isDeleteLFModalOpen.value = false;
}

async function next(close: () => void) {
  const fullLetterFile: LetterFileDto = await letterFileStore.fetchOne(props.letterFile.id);
  if (!canGoNext(fullLetterFile)) {
    close();
    return;
  }

  if (fullLetterFile.draft) {
    await letterFileStore.undraft(fullLetterFile.id);
  } else {
    await letterFileStore.next(fullLetterFile.id);
  }
  await letterFileStore.getHome();
  close();
}

const massSignatureStore = useMassSignatureStore();

const { massSignatureColors } = useLetterFileColor();
</script>
